export type HealthStatus = 'on-track' | 'at-risk' | 'delayed';

export type SchedulingStage =
  | 'Order Confirmed'
  | 'Queued'
  | 'Design'
  | 'Manufacturing'
  | 'Testing'
  | 'Production Ready'
  | 'Loading'
  | 'Shipped'
  | 'In Transit'
  | 'Delivered';

export type SchedulingAction =
  | 'expedite'
  | 'add-note'
  | 'approve'
  | 'fail'
  | 'schedule-vessel'
  | 'confirm-loaded'
  | 'reschedule';

export type ShippingDocStatus = 'pending' | 'uploaded' | 'verified';

export interface ShippingDocument {
  name: string;
  status: ShippingDocStatus;
  required: boolean;
  requiredAfterBooking?: boolean;
  file?: string;
  uploadedOn?: string;
  notes?: string;
}

export interface CarrierQuote {
  id: string;
  carrier: string;
  etaDays: number;
  cost: number;
  transit: string;
  freeDemurrageDays: number;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  liveSchedule?: boolean;
}

export interface ShippingPrepConfig {
  readiness: 'complete' | 'partial' | 'blocked';
  documents: ShippingDocument[];
  minDocs: string[]; // names required before quotes
  quotes?: CarrierQuote[];
  overrideEnabled?: boolean; // Admin override to show section early
}

export interface InitialBookingData {
  carrierId: string;
  carrierName: string;
  vesselId: string;
  vesselName: string;
  cost: number;
  etaDays: number;
  freeDemurrageDays: number;
  vesselEta: string;
  vesselEtd: string;
}

export interface StageInfo {
  name: SchedulingStage;
  index: number;
  total: number;
}

export interface SchedulingContextHeaderProps {
  orderId: string;
  customerName: string;
  plant: string;
  currentStage: SchedulingStage;
  stageIndex: number;
  totalStages: number;
  healthStatus: HealthStatus;
  promisedDate: string;
  lastUpdate: string;
  nextSystemAction: string;
  onlyShowEssentials?: boolean;
}

export interface SchedulingTimelineProps {
  stages: {
    name: SchedulingStage;
    completedAt?: string;
    startedAt?: string;
  }[];
  currentStageIndex: number;
}

export interface SchedulingDetailPanelProps {
  currentStage: SchedulingStage;
  stageDetails: Record<string, any>;
  isLoading?: boolean;
}

export interface VesselScheduleProps {
  currentStage: SchedulingStage;
  stageIndex: number;
  vessels?: {
    name: string;
    eta: string;
    cost: number;
    recommendation?: boolean;
    capacity?: string;
    containerCount?: number;
  }[];
  promisedDate: string;
  isLoading?: boolean;
}

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  type: 'system' | 'human';
  actor?: string;
  action: string;
  details?: string;
}

export interface ActivityLogProps {
  entries: ActivityLogEntry[];
  isLoading?: boolean;
}

export type NoteType = 'operational' | 'risk' | 'milestone' | 'general';
export type ImpactLevel = 'no-impact' | 'minor-risk' | 'delay-expected';
export type DelayOption = '+1d' | '+2d' | 'custom' | 'none';
export type Visibility = 'internal' | 'customer-visible';

// Reason codes for Risk/Issue notes
export type RiskCause = 'supplier' | 'design-dependency' | 'capacity' | 'quality' | 'external' | 'other';

export interface AddNotePayload {
  orderId: string;
  currentStage: SchedulingStage;
  currentHealth: HealthStatus;
  timestamp: string;
  author: string;
  noteType: NoteType;
  content: string;
  impactLevel: ImpactLevel;
  delay: DelayOption;
  delayCustom?: string;
  affectsStage?: string;
  visibility: Visibility;
  riskCause?: RiskCause;
  notifications?: {
    internal: boolean;
    customer: boolean;
    ops: boolean;
    send: boolean;
  };
  nextAction?: string;
  nextActionDueBy?: string;
  attachments?: File[];
}

export interface SchedulingAddNoteProps {
  orderId: string;
  currentStage: SchedulingStage;
  currentHealth: HealthStatus;
  timestamp: string;
  currentUser?: string;
  onSave?: (payload: AddNotePayload) => void;
  onCancel?: () => void;
}

export interface UserActionButtonProps {
  action: SchedulingAction;
  currentStage: SchedulingStage;
  disabled?: boolean;
  onClick: () => void;
}

export type ActionMap = {
  'Order Confirmed': SchedulingAction[];
  Queued: SchedulingAction[];
  Design: SchedulingAction[];
  Manufacturing: SchedulingAction[];
  Testing: SchedulingAction[];
  'Production Ready': SchedulingAction[];
  Loading: SchedulingAction[];
  Shipped: SchedulingAction[];
  'In Transit': SchedulingAction[];
  Delivered: SchedulingAction[];
};

export const SCHEDULING_STAGE_ACTION_MAP: ActionMap = {
  'Order Confirmed': ['expedite', 'add-note'],
  Queued: ['expedite', 'add-note'],
  Design: ['add-note'],
  Manufacturing: ['add-note'],
  Testing: ['approve', 'fail', 'add-note'],
  'Production Ready': ['schedule-vessel', 'add-note'],
  Loading: ['confirm-loaded', 'add-note'],
  Shipped: ['add-note'],
  'In Transit': ['add-note'],
  Delivered: [],
};
